import React, { useState, useEffect } from "react";
import { Search } from "@mui/icons-material";
import CalendorIcon from "../../../../src/assests/images/calendar-2.svg";
import arrowLeft from "../../../../src/assests/images/arrow-left.svg";
import arrowRight from "../../../../src/assests/images/arrow-right.png";
import doubleArrowLeft from "../../../../src/assests/images/double-arrow-left.svg";
import doubleArrowRight from "../../../../src/assests/images/double-arrow-right.svg";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Link,
  Typography,
  TextField,
  InputAdornment,
  Button,
  ButtonGroup,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { debounce } from "lodash";

import TaskSumm from "./TaskSumm";
import TaskTrans from "./TaskTrans";

// Existing TXN popup
import TaskTransFilterPopup, { TaskTransFilterPayload } from "./TaskTransFilterPopup";
// NEW: Summary popup
import TaskSummFilterPopup, { TaskSummFilterPayload } from "./TaskSummFilterPopup";

const CustomInput = React.forwardRef<HTMLInputElement, any>(
  ({ value, onClick, onClear }, ref) => (
    <div
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #ccc",
        backgroundColor: "white",
        borderRadius: "10px",
        padding: "0 10px",
        height: "48px",
        width: "301px",
        cursor: "pointer",
      }}
    >
      <input
        ref={ref}
        value={value || "Select Date Range"}
        style={{
          fontSize: "14px",
          border: "none",
          outline: "none",
          flex: 1,
          color: "#000",
          backgroundColor: "transparent",
        }}
        readOnly
        onClick={onClick}
      />
      {value && (
        <img
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
          src={
            "data:image/svg+xml;utf8," +
            encodeURIComponent(
              `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="#888" d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59L7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z"/></svg>`
            )
          }
          alt="Clear"
          style={{ height: 18, width: 18, cursor: "pointer", marginRight: 8 }}
        />
      )}
      <img
        src={CalendorIcon}
        alt="Calendar Icon"
        style={{ height: "20px", width: "20px", cursor: "pointer" }}
        onClick={onClick}
      />
    </div>
  )
);
CustomInput.displayName = "CustomInput";

function ParentTask() {
  // --- MINIMAL CHANGE: keep two separate header ranges to avoid overlap ---
  const [trxRange, setTrxRange] = useState<[Date | null, Date | null]>([null, null]);   // header for Transactions
  const [summRange, setSummRange] = useState<[Date | null, Date | null]>([null, null]); // header for Summary

  // Search + debounce shared by both views
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const handleDebounce = debounce((q: string) => setDebouncedSearch(q), 300);
    handleDebounce(searchQuery);
    return () => handleDebounce.cancel();
  }, [searchQuery]);

  // Toggle: false = Summary, true = Transactions
  const [isSwitch, setIsSwitch] = useState(false);

  // ===== Transactions popup state =====
  const [isTxnFilterOpen, setIsTxnFilterOpen] = useState(false);
  const [shouldFetchTxnFilters, setShouldFetchTxnFilters] = useState(false);
  const [appliedTxnFilters, setAppliedTxnFilters] = useState<TaskTransFilterPayload | null>(null);

  const sanitizedAppliedTxnFilters = React.useMemo(() => {
    if (!appliedTxnFilters) return undefined;
    const x = appliedTxnFilters;
    return {
      ...(x.locations != null ? { locations: x.locations || undefined } : {}),
      ...(x.outlet_category != null ? { outlet_category: x.outlet_category || undefined } : {}),
      ...(x.pmi_entity_code != null ? { pmi_entity_code: x.pmi_entity_code || undefined } : {}),
      ...(x.outlet_name != null ? { outlet_name: x.outlet_name || undefined } : {}),
      // Dates come from header (trxRange)
    };
  }, [appliedTxnFilters]);

  // ===== Summary popup state =====
  const [isSummFilterOpen, setIsSummFilterOpen] = useState(false);
  const [shouldFetchSummFilters, setShouldFetchSummFilters] = useState(false);
  const [appliedSummFilters, setAppliedSummFilters] = useState<TaskSummFilterPayload | null>(null);

  const sanitizedAppliedSummFilters = React.useMemo(() => {
    if (!appliedSummFilters) return undefined;
    const x = appliedSummFilters;
    const out: any = {};
    if (Array.isArray(x.outlet_name) && x.outlet_name.length) out.outlet_name = x.outlet_name;
    if (Array.isArray(x.pmi_entity_code) && x.pmi_entity_code.length) out.pmi_entity_code = x.pmi_entity_code;
    if (Array.isArray(x.task_type_en) && x.task_type_en.length) out.task_type_en = x.task_type_en;
    if (x.search_key && x.search_key.trim()) out.search_key = x.search_key.trim();
    // Dates will be injected from header (summRange) at render time.
    return out;
  }, [appliedSummFilters]);

  const SEARCH_PLACEHOLDERS = {
    summary: "Search by Outlet Name, PMI Entity Code, User Name, Mobile Number",
    transactions: "Search by User Name, Outlet Name, PMI Entity Code",
  };
  const searchPlaceholder = isSwitch ? SEARCH_PLACEHOLDERS.transactions : SEARCH_PLACEHOLDERS.summary;

  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.35)",
    zIndex: 2000,
    padding: 16,
  };

  const formatRangeText = (start: Date | null, end: Date | null) => {
    if (!start || !end) return "";
    const opts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short", year: "numeric" };
    const s = start.toLocaleDateString(undefined, opts);
    const e = end.toLocaleDateString(undefined, opts);
    return `${s} to ${e}`;
  };

  // Helpers to bind header to active tab with MINIMAL changes
  const activeRange = isSwitch ? trxRange : summRange;
  const [activeStart, activeEnd] = activeRange;
  const setActiveRange = isSwitch ? setTrxRange : setSummRange;

  const toYMD = (d: Date | null) => {
    if (!d) return undefined;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  return (
    <Box sx={{ background: "#fff", paddingTop: "30px" }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 32px" }}>
        <Box>
          <Link
            href="#"
            sx={{ textDecoration: "none", fontSize: "14px", fontWeight: "500", color: "#696A6B", paddingRight: "10px" }}
          >
            Task Report
          </Link>
          <Typography variant="h3" sx={{ fontSize: "24px", fontWeight: "600", color: "#000", marginTop: "16px" }}>
            Task Report
          </Typography>
        </Box>

        {/* Filter + Toggle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={() => {
              if (isSwitch) {
                setIsTxnFilterOpen(true);
                setShouldFetchTxnFilters(true);
              } else {
                setIsSummFilterOpen(true);
                setShouldFetchSummFilters(true);
              }
            }}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              bgcolor: "#C8172F",
              color: "#fff",
              borderColor: "#C8172F",
              borderRadius: "8px",
              height: "40px",
              padding: "0px 18px",
              "&:hover": { bgcolor: "#a31425", borderColor: "#a31425" },
            }}
          >
            Filter
          </Button>

          <ButtonGroup variant="outlined" sx={{ mr: 0 }}>
            <Button
              onClick={() => setIsSwitch(false)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: !isSwitch ? "#C8172F" : "transparent",
                color: !isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "40px",
                "&:hover": {
                  bgcolor: !isSwitch ? "#a31425" : "rgba(200,23,47,0.1)",
                  borderColor: "#a31425",
                },
              }}
            >
              Summary
            </Button>
            <Button
              onClick={() => setIsSwitch(true)}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                bgcolor: isSwitch ? "#C8172F" : "transparent",
                color: isSwitch ? "#fff" : "#000",
                borderColor: "#C8172F",
                borderRadius: "8px",
                height: "40px",
                "&:hover": {
                  bgcolor: isSwitch ? "#a31425" : "rgba(200,23,47,0.1)",
                  borderColor: "#a31425",
                },
              }}
            >
              Transactions
            </Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Search + Date */}
      <Box sx={{ display: "flex", justifyContent: "space-between", marginTop: "16px", padding: "0 32px 30px" }}>
        <Box sx={{ maxWidth: "520px", flexGrow: 1 }}>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={searchPlaceholder}
            type="search"
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ height: "20px", width: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                "& fieldset": { border: "2px solid #F5F5F5" },
                height: "48px",
              },
            }}
          />
        </Box>

        {/* Header date range now switches per tab (trx vs summ) */}
        <Box sx={{ display: "flex" }}>
          <Box>
            <DatePicker
              selectsRange
              startDate={activeStart || undefined}
              endDate={activeEnd || undefined}
              onChange={(update: [Date | null, Date | null] | null) => setActiveRange(update ?? [null, null])}
              customInput={
                <CustomInput
                  onClear={() => {
                    setActiveRange([null, null]);
                    if (isSwitch) {
                      setAppliedTxnFilters((prev) =>
                        prev ? { ...prev, transaction_from: null, transaction_to: null } : prev
                      );
                    } else {
                      setAppliedSummFilters((prev) =>
                        prev
                          ? { ...prev, task_start_from: null, task_end_to: null }
                          : prev
                      );
                    }
                  }}
                  value={formatRangeText(activeStart, activeEnd)}
                />
              }
              renderCustomHeader={(hdr: any) => {
                const { date, decreaseMonth, increaseMonth, decreaseYear, increaseYear } = hdr;
                return (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <button onClick={decreaseYear} aria-label="Previous year">
                      <img src={doubleArrowLeft} alt="Previous year" />
                    </button>
                    <button onClick={decreaseMonth} aria-label="Previous month">
                      <img src={arrowLeft} alt="Previous month" />
                    </button>

                    <span style={{ margin: "0 10px" }}>
                      {date.toLocaleString("default", { year: "numeric", month: "long" })}
                    </span>

                    <button onClick={increaseMonth} aria-label="Next month">
                      <img src={arrowRight} alt="Next month" />
                    </button>
                    <button onClick={increaseYear} aria-label="Next year">
                      <img src={doubleArrowRight} alt="Next year" />
                    </button>
                  </div>
                );
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Tables */}
      <Box sx={{ width: "100%", background: "#f9fbfc", borderTop: "1px solid #d3d3d3" }}>
        {isSwitch ? (
          <TaskTrans
            dateRange={trxRange}                  // ⬅ uses trx header range
            searchQuery={debouncedSearch}
            appliedFilters={sanitizedAppliedTxnFilters}
          />
        ) : (
          <TaskSumm
            searchQuery={debouncedSearch}
            appliedFilters={{
              ...sanitizedAppliedSummFilters,
              // ⬅ inject summary header dates for API
              ...(summRange[0] ? { task_start_from: toYMD(summRange[0]) } : {}),
              ...(summRange[1] ? { task_end_to: toYMD(summRange[1]) } : {}),
            }}
          />
        )}
      </Box>

      {/* POPUPS */}
      {/* Transactions popup */}
      {isTxnFilterOpen && (
        <div
          style={overlayStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsTxnFilterOpen(false);
          }}
        >
          <TaskTransFilterPopup
            isOpen={isTxnFilterOpen}
            shouldFetch={shouldFetchTxnFilters}
            onFetched={() => setShouldFetchTxnFilters(false)}
            onApplyFilters={(payload) => {
              setAppliedTxnFilters(payload);
              setIsTxnFilterOpen(false);
            }}
            onCancel={() => setIsTxnFilterOpen(false)}
            initialFilters={appliedTxnFilters}
            dateRange={trxRange}          // ⬅ bind popup to trx header
            setDateRange={setTrxRange}    // ⬅ sync both ways
          />
        </div>
      )}

      {/* Summary popup */}
      {isSummFilterOpen && (
        <div
          style={overlayStyle}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsSummFilterOpen(false);
          }}
        >
          <TaskSummFilterPopup
  isOpen={isSummFilterOpen}
  shouldFetch={shouldFetchSummFilters}
  onFetched={() => setShouldFetchSummFilters(false)}
  onApplyFilters={(payload) => {
    setAppliedSummFilters(payload);
    setIsSummFilterOpen(false);
  }}
  onCancel={() => setIsSummFilterOpen(false)}
  initialFilters={appliedSummFilters}
  // bind popup to summary header range (same behavior as TXN)
  dateRange={{ start: summRange[0], end: summRange[1] }}
  setDateRange={(next) => {
    // handle both object and updater function shapes
    const current = { start: summRange[0], end: summRange[1] };
    const v =
      typeof next === "function"
        ? (next as (prev: typeof current) => typeof current)(current)
        : next;

    setSummRange([v?.start ?? null, v?.end ?? null]);
  }}
/>

        </div>
      )}
      <Box
        sx={{
          mt: 6,
          px: 4,
          py: 2,
          borderTop: "1px solid #eee",
          backgroundColor: "#fff",
        }}
      >
        <Button
          variant="text"
          startIcon={<ArrowBackIosNewIcon fontSize="small" />}
          onClick={() => window.history.back()}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            color: "#C8172F",
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Back
        </Button>
      </Box>
    </Box>
  );
}

export default ParentTask;
